import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { Smartphone, TabletSmartphone, Headphones, RefreshCw } from "lucide-react";

const defaultCategories = [
  { name: "iPhone", icon: Smartphone, slug: "iphone", color: "from-primary/10 to-accent/10" },
  { name: "Samsung", icon: TabletSmartphone, slug: "samsung", color: "from-accent/10 to-primary/10" },
  { name: "Xiaomi", icon: Smartphone, slug: "xiaomi", color: "from-primary/10 to-primary/5" },
  { name: "Used Phones", icon: RefreshCw, slug: "used", color: "from-accent/10 to-accent/5" },
  { name: "Accessories", icon: Headphones, slug: "accessories", color: "from-primary/5 to-accent/10" },
];

interface Props {
  categories: any[];
  isLoading?: boolean;
}

export const CategoriesSection = ({ categories, isLoading }: Props) => {
  const { t } = useTranslation();
  
  // Combine DB categories with defaults if DB is empty, or just use defaults if loading
  const displayCats = categories && categories.length > 0
    ? categories.map((c) => ({ 
        id: c.id || c.slug, 
        name: c.name, 
        icon: Smartphone, 
        color: "from-primary/10 to-accent/10" 
      }))
    : defaultCategories.map((c) => ({ id: c.slug, ...c }));

  if (isLoading && categories.length === 0) {
    return (
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16 animate-pulse">
            <div className="h-4 w-20 bg-muted rounded mx-auto mb-2" />
            <div className="h-10 w-64 bg-muted rounded mx-auto" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-40 rounded-2xl bg-muted animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <ScrollReveal>
          <div className="text-center mb-10 sm:mb-16">
            <span className="text-xs font-semibold text-primary tracking-[0.2em] uppercase">{t("sections.browse")}</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold mt-2 sm:mt-3 text-foreground">{t("sections.shopByCategory")}</h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-2 sm:mt-3 max-w-md mx-auto">{t("sections.shopByCategoryDesc")}</p>
          </div>
        </ScrollReveal>
        <StaggerContainer className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
          {displayCats.map((cat: any) => {
            const Icon = cat.icon || Smartphone;
            return (
              <StaggerItem key={cat.id}>
                <Link to={`/shop?category=${cat.id}`}>
                  <motion.div whileHover={{ y: -4, scale: 1.02 }} transition={{ duration: 0.2 }}>
                    <div className={`text-center rounded-xl border border-border hover:border-primary/30 hover:shadow-lg transition-all cursor-pointer group bg-gradient-to-br ${cat.color} p-3 sm:p-4`}>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-2 group-hover:bg-primary/20 group-hover:scale-105 transition-all duration-300">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <p className="font-semibold text-foreground text-[10px] sm:text-xs tracking-tight line-clamp-1">{cat.name}</p>
                    </div>
                  </motion.div>
                </Link>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </div>
    </section>
  );
};
